import React, { useEffect } from 'react';
import { Card, CardContent, Grid, makeStyles } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
// eslint-disable-next-line import/no-named-as-default
import DOMPurify from 'dompurify';

import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Heading, Typography, Link } from '@klp/design';

import { messageApiRoutes } from '../api';
import type { Message } from '../classes';
import { MessageBoardRoutes } from '../config';
import { useGetMessageBoardSummaryQuery } from '../rtkApi';

const useStyles = makeStyles((theme: Theme) => ({
    cardContentGrid: {
        justifyContent: 'space-between' as const,
        alignItems: 'flex-end' as const,
    },
    card: {
        padding: theme.spacing(3),
        borderRadius: theme.spacing(0.2),
    },
    cardActions: {
        marginTop: theme.spacing(4),
    },
}));

interface IMessageBoardSummaryProps {
    trigger?: Date;
}

export const MessageBoardSummary: React.FC<IMessageBoardSummaryProps> = ({
    trigger,
}) => {
    const classes = useStyles();
    const { selectedClient } = useAppParams();

    const { data, refetch } = useGetMessageBoardSummaryQuery(
        selectedClient
            ? {
                  kundeNummer: selectedClient.kundeNummer,
                  numberOfMessages: 3,
              }
            : {
                  numberOfMessages: 3,
              },
    );

    const messageList = data?.messageList || [];

    useEffect(() => {
        if (trigger) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trigger]);

    const renderMessageRow = (message: Message) => {
        return (
            <Grid
                key={message.id}
                item={true}
                xs={12}
                sm={6}
                md={4}
                className={classes.card}
            >
                <Card className={classes.card}>
                    <CardContent>
                        <Grid
                            container={true}
                            className={classes.cardContentGrid}
                        >
                            <Heading klpHeadingLevel="TWO">
                                {message.title}
                            </Heading>
                            <Typography marginTop="compact">
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            message.content,
                                        ),
                                    }}
                                />
                            </Typography>
                            <Block>
                                <Link
                                    href={messageApiRoutes.single(
                                        message.id || 0,
                                    )}
                                    klpTypographyVariant="small"
                                    marginTop="compact"
                                    translation="message.readMore"
                                />
                            </Block>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    return messageList && messageList.length !== 0 ? (
        <Block paddingTop="massive">
            <Heading
                klpHeadingLevel="ONE"
                translation="frontPage.messages"
                align="center"
            />
            <Grid container={true} justifyContent="center" direction="row">
                {messageList.map((message) => renderMessageRow(message))}
                <Grid container={true} justifyContent="center" direction="row">
                    <Link
                        href={MessageBoardRoutes.moduleRoute()}
                        translation="frontPage.messagesLink"
                        target="_blank"
                    />
                </Grid>
            </Grid>
        </Block>
    ) : null;
};
